import { storeToRefs } from "pinia";
import { computed, reactive, ref, toRefs } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import random from "shared/helpers/random";
import { Contact } from "shared/resources";
import useAIStore, { type AISuggestedContactsFilter } from "shared/stores/ai";
import { type Nullable } from "shared/types/utility";

import useContactsSearchFiltering from "./useContactsSearchFiltering";

export type SuggestedContactType = "influencer" | "outlet" | "all";

export interface SuggestedContactOptions {
  contactType?: SuggestedContactType;
  sortField?: "name" | "relevance";
  sortDirection?: "asc" | "desc";
}

export default function useSuggestedContacts(
  options?: SuggestedContactOptions
) {
  const contactOptions = toRefs(options || {});

  const searchFilteringOptions = reactive({
    contactType: contactOptions.contactType,
  });

  const { contactIncludes } = useContactsSearchFiltering(
    searchFilteringOptions
  );

  const aiStore = useAIStore();

  const { aiSuggestedContactIds } = storeToRefs(aiStore);

  const searchType = computed<SuggestedContactType>(
    () => contactOptions.contactType?.value || "influencer"
  );

  const loadingContacts = ref<boolean>(false);
  const localContacts = ref<Contact[]>([]);

  let abortController: Nullable<AbortController> = null;

  const contacts = computed<Contact[]>(() => {
    const filteredContacts = localContacts.value.filter(
      (contact) => !aiSuggestedContactIds.value.includes(contact.documentId)
    );

    if (contactOptions.sortField?.value === "name") {
      const direction = contactOptions.sortDirection?.value === "desc" ? -1 : 1;

      return filteredContacts.toSorted(
        (contactA, contactB) =>
          direction * contactA.name.localeCompare(contactB.name)
      ) as Contact[];
    }

    if (contactOptions.sortDirection?.value === "asc") {
      return filteredContacts.toReversed() as Contact[];
    }

    return filteredContacts as Contact[];
  });

  const searchQuery = computed(() => {
    const influencerQuery = {
      bool: {
        must: [
          {
            terms: {
              type: ["influencer"],
            },
          },
          {
            nested: {
              path: "outlets",
              ignore_unmapped: true,
              query: {
                bool: {
                  must: [
                    {
                      terms: {
                        "outlets.monitoring_only_flag": [false],
                      },
                    },
                  ],
                },
              },
            },
          },
        ],
      },
    };

    const outletQuery = {
      bool: {
        must: [
          {
            terms: {
              type: ["outlet"],
            },
          },
          {
            terms: {
              monitoring_only_flag: [false],
            },
          },
        ],
        must_not: [
          {
            terms: {
              parent_relationship: ["desk"],
            },
          },
        ],
      },
    };

    if (searchType.value === "all") {
      return {
        bool: {
          must: [
            {
              bool: {
                minimum_should_match: 1,
                should: [outletQuery, influencerQuery],
              },
            },
          ],
        },
      };
    }

    if (searchType.value === "outlet") {
      return outletQuery;
    }

    return influencerQuery;
  });

  async function loadContacts(contactFilters: AISuggestedContactsFilter = {}) {
    try {
      if (abortController) {
        abortController.abort();
      }

      abortController = new AbortController();

      loadingContacts.value = true;

      const response = await Contact.extraFetchOptions({
        signal: abortController.signal,
      })
        .where({
          query: JSON.stringify(searchQuery.value),
          ...contactFilters,
        })
        .extraParams({
          cacheKiller: random(),
        })
        .includes(contactIncludes.value)
        .page(1)
        .per(searchType.value === "all" ? 200 : 100)
        .all();

      localContacts.value = response.data;
    } catch (error) {
      ErrorDialog(
        getLocaleText("ai_suggested_contacts_panel.load_error"),
        error
      );
    } finally {
      abortController = null;
      loadingContacts.value = false;
    }
  }

  return {
    contacts,
    loadingContacts,

    searchQuery,
    loadContacts,
  };
}
